import { useMemo } from 'react';
import { Box, Button, Message } from 'components/ui';
import { Col, Row } from 'react-grid-system';
import uniqBy from 'lodash/uniqBy';
import sortBy from 'lodash/sortBy';
import FuturaItemPreview from './FuturaItemPreview';
import { Attribute, Item } from 'utils/types';
import InfiniteScroll from 'react-infinite-scroll-component';
import { filterItems } from 'utils/futura';
import { FUTURA_ATTRIBUTES, REQUEST_STATUS } from '../constants';
import ItemListLoading from './ItemListLoading';
import { useInfiniteQuery } from 'react-query';
import { fetchExternalCollectionItems } from 'utils/platformPublicApi';
import { useWallet } from 'components/WalletContext';
import { useAllOwnedNfts } from 'hooks/useEbApi';
import { futuraChainId, futuraContractAddress } from 'utils/config';
import take from 'lodash/take';
import flatten from 'lodash/flatten';

interface CollectionItemsProps {
  collectionId: string;
  checked: string[];
  onlyOwned?: boolean;
}

const PAGE_LIMIT = 24;

const CollectionItemsList = ({
  collectionId,
  checked,
  onlyOwned = false,
}: CollectionItemsProps) => {
  const { address, selectWallet } = useWallet();
  const {
    data: owned,
    status: ownedStatus,
    isLoading: ownedIsLoading,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ...rest
  } = useAllOwnedNfts(address, [futuraChainId], [futuraContractAddress], false, {
    enabled: Boolean(address),
  });

  const ownedTokenIds = useMemo(() => {
    if (!address) return [];

    const allTokenIds = (owned ?? []).map(item => item.token_id);
    // limit number of tokens or else header is too large
    return take(allTokenIds, 264);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, owned?.length]);

  let attributes: Attribute[] = checked.map(checkedItem => {
    let parsed = checkedItem.split('-');
    let attribute = { name: parsed[0], value: parsed[1] };
    return attribute;
  });

  const { data, fetchNextPage, hasNextPage, status } = useInfiniteQuery(
    ['items', collectionId, attributes, onlyOwned ? ownedTokenIds : []],
    ({ pageParam = '' }) => {
      return fetchExternalCollectionItems(
        collectionId,
        attributes,
        onlyOwned ? ownedTokenIds : undefined,
        PAGE_LIMIT,
        pageParam
      );
    },
    {
      getNextPageParam: (lastPage, pages) => (lastPage.has_more ? lastPage?.cursor : undefined),
      keepPreviousData: true,
      staleTime: 1000 * 60 * 2, // 2 minutes
      cacheTime: 1000 * 60 * 5, // 5 minutes
    }
  );

  const displayItems = useMemo(() => {
    if (onlyOwned && ownedTokenIds.length === 0) {
      return [];
    }

    const allItems = flatten((data?.pages ?? []).map(page => page.results));
    const sortedItems = sortBy(uniqBy(allItems, 'token_id'), [
      item => parseInt(item.attributes[FUTURA_ATTRIBUTES.generation]),
      item => parseInt(item.token_id),
    ]);
    const filteredItems = filterItems(sortedItems, attributes);
    return filteredItems;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked, data?.pages, onlyOwned, ownedTokenIds.length]);

  const useMyMixes = onlyOwned && Boolean(address);

  const emptyPage = useMyMixes
    ? ownedTokenIds.length === 0 || displayItems.length === 0
    : displayItems.length === 0 &&
      ((data?.pages?.length || 0) === 0 || data?.pages[0].total_results === 0);

  const isLoading = useMyMixes
    ? status === REQUEST_STATUS.loading || ownedIsLoading
    : status === REQUEST_STATUS.loading;

  const isError = useMyMixes
    ? status === REQUEST_STATUS.error || (ownedStatus === REQUEST_STATUS.error && !ownedIsLoading)
    : status === REQUEST_STATUS.error;

  const isSuccess = useMyMixes
    ? status === REQUEST_STATUS.success && ownedStatus === REQUEST_STATUS.success && !ownedIsLoading
    : status === REQUEST_STATUS.success;

  return (
    <Box margin="0 auto">
      <Box textAlign="center">
        {isLoading && <ItemListLoading />}

        {isError && (
          <Message maxWidth="420px" margin="0 auto" color="black">
            Error loading items.
          </Message>
        )}
      </Box>

      {!address && onlyOwned ? (
        <Button onClick={selectWallet} margin="8px auto">
          Connect Wallet
        </Button>
      ) : isSuccess && emptyPage ? (
        <Message maxWidth="420px" margin="0 auto" color="black">
          No results found.
        </Message>
      ) : (
        <></>
      )}

      {isSuccess && displayItems.length > 0 && (
        <InfiniteScroll
          height="calc(100vh - 180px)"
          dataLength={displayItems.length}
          next={fetchNextPage}
          hasMore={Boolean(hasNextPage)}
          loader={
            <Box width="100%" textAlign="center">
              <ItemListLoading />
            </Box>
          }
        >
          <Box pb={4}>
            <Row>
              {(displayItems || []).map((item: Item) => (
                <Col key={item.token_id} sm={12} md={6} lg={4} xl={3} xxl={2}>
                  <FuturaItemPreview item={item} />
                </Col>
              ))}
            </Row>
          </Box>
        </InfiniteScroll>
      )}
    </Box>
  );
};

export default CollectionItemsList;
